import React, { useEffect, useState, useRef, useCallback } from "react";
import { useResponsiveHeight } from "../../../hooks/useResponsiveHeight";
import { useSelector, useDispatch } from "react-redux";
import CoachDetails from "./CoachDetails";
import LoadingSpinner from "../../../components/LoadingSpinner";
import CoachesList from "../../common/coaches/CoachesList";
import SearchCoaches from "../../common/coaches/SearchCoaches";
import { useAxiosInstance } from "../../../services/Interceptor";
import { selectCoach } from "../../../store/slices/coachSlice";
import { selectedCoachAction } from "../../../store/CoachActions";
import { useSearch } from "../../../hooks/useSearch";

const ITEMS_PER_PAGE = 20;

function AdminCoaches() {
  const dispatch = useDispatch();
  const r_coaches = useSelector((state) => state.coaches);
  const { coaches, loading, error, selectedCoach, coachDetails } = r_coaches;
  const apiInstance = useAxiosInstance();
  const [isCoachSelected, setIsCoachSelected] = useState(false);

  const [allFilteredCoaches, setAllFilteredCoaches] = useState([]);
  const [displayedCoaches, setDisplayedCoaches] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [search, setSearch] = useState("");

  const observerTarget = useRef(null);

  const { adjustedHeight } = useResponsiveHeight({
    mobileOffset: 290,
    tabletOffset: 230,
    desktopOffset: 230,
  });

  const filteredCoaches = useSearch(coaches, search, [
    "name",
    "lastname",
    "university",
  ]);

  // Load more coaches for infinite scroll
  const loadMoreCoaches = useCallback(() => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);

    const nextPage = currentPage + 1;
    const startIndex = nextPage * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;

    const nextBatch = allFilteredCoaches.slice(startIndex, endIndex);

    if (nextBatch.length > 0) {
      setDisplayedCoaches((prev) => [...prev, ...nextBatch]);
      setCurrentPage(nextPage);
      setHasMore(endIndex < allFilteredCoaches.length);
    } else {
      setHasMore(false);
    }

    setIsLoadingMore(false);
  }, [isLoadingMore, hasMore, currentPage, allFilteredCoaches]);

  // Reset pagination helper
  const resetPagination = useCallback((coachesData) => {
    const firstPage = coachesData.slice(0, ITEMS_PER_PAGE);
    setAllFilteredCoaches(coachesData);
    setDisplayedCoaches(firstPage);
    setCurrentPage(0);
    setHasMore(coachesData.length > ITEMS_PER_PAGE);
  }, []);

  useEffect(() => {
    resetPagination(filteredCoaches);
  }, [filteredCoaches, resetPagination]);

  // Fetch selected coach details when selection changes
  useEffect(() => {
    if (selectedCoach) {
      dispatch(selectedCoachAction(apiInstance, selectedCoach.userId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCoach, dispatch]);

  // Update local coach list when store changes
  useEffect(() => {
    if (coaches) {
      resetPagination(coaches);
    }
  }, [coaches, resetPagination]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !isLoadingMore &&
          !loading
        ) {
          loadMoreCoaches();
        }
      },
      { threshold: 0.1 },
    );

    const currentTarget = observerTarget.current;

    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [hasMore, isLoadingMore, loading, loadMoreCoaches]);

  const handleSelectCoach = (coach) => {
    dispatch(selectCoach(coach));
    setIsCoachSelected(true);
  };

  // Toggle back from detail view
  const backButtonClickEvent = (value) => {
    setIsCoachSelected(value);
  };

  return (
    <div className="p-4 bg-gray-100 h-full">
      <div className="flex flex-col xl:flex-row lg:flex-row gap-4">
        <div className="w-full lg:w-2/5 xl:w-2/5">
          <div className="bg-white shadow-lg rounded-lg border-gray-300 h-full p-4 pb-0">
            <h1 className="text-xl mb-5 uppercase">COACHES</h1>
            <SearchCoaches searchCoachFn={(e) => setSearch(e.target.value)} />
            {loading && <LoadingSpinner />}
            {error && <p className="text-red-500">{error}</p>}

            {/* Scrollable list container with dynamic height */}
            <div
              className="mt-4 pr-4 -mr-4 overflow-y-auto"
              style={{ height: `${adjustedHeight}px` }}
            >
              {!loading && displayedCoaches && displayedCoaches.length > 0 ? (
                <>
                  {displayedCoaches.map((coach) => (
                    <div
                      key={coach.id || coach._id || coach.userId}
                      className={`cursor-pointer ${
                        selectedCoach?.id === coach.id
                          ? "border-blue-500"
                          : "border-black"
                      }`}
                      onClick={() => handleSelectCoach(coach)}
                    >
                      <CoachesList coach={coach} showArrow={true} />
                    </div>
                  ))}

                  {/* Intersection Observer sentinel */}
                  <div ref={observerTarget} style={{ height: "1px" }} />

                  {/* Loading more indicator */}
                  {isLoadingMore && (
                    <div className="flex justify-center py-4">
                      <div className="text-gray-500 text-sm">
                        Loading more coaches...
                      </div>
                    </div>
                  )}

                  {/* End of list message */}
                  {!hasMore && displayedCoaches.length > 0 && (
                    <div className="flex justify-center py-4">
                      <div className="text-gray-400 text-sm">
                        All coaches loaded ({displayedCoaches.length} total)
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="h-full flex items-center justify-center ">
                  <div className=" w-full flex flex-col items-center text-gray-800">
                    <img
                      alt="Athlete Name"
                      src="/nodata.gif"
                      className="object-cover h-20 ml-18 pb-3"
                    ></img>
                    <p className="text-center text-[#8E8E93] text-1xl">
                      There are no coaches that match your search.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Coach detail panel */}
        {isCoachSelected && (
          <div className="w-full lg:w-3/5 xl:w-3/5">
            {selectedCoach && coachDetails ? (
              <CoachDetails
                selectedCoach={selectedCoach}
                athletesLiked={coachDetails.athletesLiked}
                backButtonClickEvent={backButtonClickEvent}
              />
            ) : (
              <div className="bg-gray-100 h-full flex items-center justify-center">
                <div className="w-full p-2 mt-5 flex flex-col items-center">
                  <img
                    alt="Athlete Name"
                    src="/select_row.svg"
                    className="object-cover h-20 ml-18 pb-3"
                  />
                  <p className="text-center text-[#8E8E93] text-1xl">
                    Please select a Coach to view their Profile
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminCoaches;
